import React, {
  CSSProperties,
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';

import styles from './styles.module.css';
import { ShowSnackbarOptions } from './types';

const DEFAULT_OFFSET = 12;
const VIEWPORT_GUTTER = 12;
const FALLBACK_WIDTH = 320;

type SnackbarProps = {
  onDismissRequest: () => void;
} & Required<Pick<ShowSnackbarOptions, 'title' | 'variant' | 'position'>> &
  Omit<ShowSnackbarOptions, 'title' | 'variant' | 'position'>;

const getAnchorElement = (
  anchorRef?: ShowSnackbarOptions['anchorRef']
): HTMLElement | null => {
  if (!anchorRef) {
    return null;
  }

  if ('current' in anchorRef) {
    return anchorRef.current;
  }

  return anchorRef;
};

export const Snackbar: FC<SnackbarProps> = ({
  title,
  content,
  variant,
  position,
  anchorRef,
  offset,
  className,
  onDismiss,
  onDismissRequest,
}) => {
  const snackbarRef = useRef<HTMLDivElement>(null);
  const anchorElement = getAnchorElement(anchorRef);
  const [anchorStyle, setAnchorStyle] = useState<CSSProperties | undefined>();

  const isAnchored = Boolean(anchorElement);

  const handleDismiss = useCallback(() => {
    try {
      onDismiss?.();
    } finally {
      onDismissRequest();
    }
  }, [onDismiss, onDismissRequest]);

  const updateAnchorPosition = useCallback(() => {
    if (!anchorElement) {
      setAnchorStyle(undefined);
      return;
    }

    const anchorRect = anchorElement.getBoundingClientRect();
    const snackbarWidth = snackbarRef.current?.offsetWidth || FALLBACK_WIDTH;
    const halfWidth = snackbarWidth / 2;
    const targetLeft = anchorRect.left + anchorRect.width / 2;
    const minLeft = VIEWPORT_GUTTER + halfWidth;
    const maxLeft = window.innerWidth - VIEWPORT_GUTTER - halfWidth;
    const clampedLeft = Math.min(Math.max(targetLeft, minLeft), maxLeft);
    const top = anchorRect.bottom + (offset ?? DEFAULT_OFFSET);

    setAnchorStyle({
      top: `${Math.round(top)}px`,
      left: `${Math.round(clampedLeft)}px`,
      transform: 'translateX(-50%)',
    });
  }, [anchorElement, offset]);

  useLayoutEffect(() => {
    if (!anchorElement) {
      return;
    }

    updateAnchorPosition();
  }, [anchorElement, updateAnchorPosition, title, content, variant]);

  useEffect(() => {
    if (!anchorElement) {
      return;
    }

    const handleViewportUpdate = () => updateAnchorPosition();
    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(handleViewportUpdate)
        : null;

    window.addEventListener('resize', handleViewportUpdate);
    window.addEventListener('scroll', handleViewportUpdate, true);

    resizeObserver?.observe(anchorElement);

    if (snackbarRef.current) {
      resizeObserver?.observe(snackbarRef.current);
    }

    return () => {
      window.removeEventListener('resize', handleViewportUpdate);
      window.removeEventListener('scroll', handleViewportUpdate, true);
      resizeObserver?.disconnect();
    };
  }, [anchorElement, updateAnchorPosition]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleDismiss();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleDismiss]);

  const liveRegionProps =
    variant === 'error'
      ? { role: 'alert', 'aria-live': 'assertive' as const }
      : { role: 'status', 'aria-live': 'polite' as const };

  const fixedPositionStyle =
    !isAnchored && typeof offset === 'number'
      ? ({ [position]: `${offset}px` } as CSSProperties)
      : undefined;

  return (
    <div
      ref={snackbarRef}
      className={classNames(
        styles.snackbar,
        styles[position],
        styles[variant],
        {
          [styles.anchored]: isAnchored,
        },
        className
      )}
      style={isAnchored ? anchorStyle : fixedPositionStyle}
      aria-atomic="true"
      {...liveRegionProps}
    >
      <div className={styles.message}>
        <p className={styles.title}>{title}</p>
        {content ? <div className={styles.content}>{content}</div> : null}
      </div>
      <button
        type="button"
        className={styles.dismissButton}
        onClick={handleDismiss}
      >
        Dismiss
      </button>
    </div>
  );
};
